
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../components/docs/ContentSwitcher';
import SampleCode from '../../../components/docs/plaidoms/SampleCode';
import Terminal from 'plaid-threads/Icons/Terminal';
import Video from 'plaid-threads/Icons/Video';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: "Learn how to set up Auth, which is used for connecting to users' bank accounts and powering ACH, European, and Canadian (EFT) transfers of funds."
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Add Auth to your app' subtitle='Use Auth to connect user bank accounts' mdxType="Header" />
    <p>{`In this guide, we'll demonstrate how to add `}<a parentName="p" {...{
        "href": "/docs/api/products/auth/"
      }}>{`Auth`}</a>{` to your app so that you can connect to your users' bank accounts and obtain the information needed to set up funds transfers.`}</p>
    <p>{`If you're already familiar with using Plaid and are set up to make calls to the Plaid API, see `}<a parentName="p" {...{
        "href": "#getting-auth-data"
      }}>{`Getting Auth data`}</a>{`. If you're interested in using a Plaid partner, such as Stripe or Dwolla, to process payments, see `}<a parentName="p" {...{
        "href": "#moving-funds-with-a-payment-partner"
      }}>{`Moving funds with a payment partner`}</a>{`.`}</p>
    <Callout icon={<Video mdxType="Video" />} mdxType="Callout">
      <p>{`Prefer to learn by watching? A `}<a parentName="p" {...{
          "href": "https://youtu.be/FlZ5nzlIq74"
        }}>{`video guide`}</a>{` is
available for this topic.`}</p>
    </Callout>
    <h4 {...{
      "id": "get-plaid-api-keys-and-complete-application-and-company-profile"
    }}>{`Get Plaid API keys and complete application and company profile`}</h4>
    <p>{`If you don't already have one, you'll need to `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signup"
      }}>{`create a Plaid developer account`}</a>{`. After creating your account, you can find your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`API keys`}</a>{` under Build in the Plaid Dashboard.`}</p>
    <p>{`You will also need to complete your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/app-branding"
      }}>{`application profile`}</a>{` and `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/profile"
      }}>{`company profile`}</a>{` in the Dashboard. The information in your profile will be shared with users of your application when they manage their connection on the `}<a parentName="p" {...{
        "href": "https://my.plaid.com"
      }}>{`Plaid Portal`}</a>{`. Your application profile and company profile must be completed before connecting to certain institutions in Production.`}</p>
    <h4 {...{
      "id": "install-and-initialize-plaid-libraries"
    }}>{`Install and initialize Plaid libraries`}</h4>
    <p>{`You can use our official server-side client libraries to connect to the Plaid API from your application:`}</p>
    <SampleCode.InstallClientLibraries />
    <p>{`After you've installed Plaid's client libraries, you can initialize them by passing in your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, and the environment you wish to connect to (Sandbox or Production). This will make sure the client libraries pass along your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` with each request, and you won't need to explicitly include them in any other calls.`}</p>
    <SampleCode.InitializeClientLibraries />
    <h4 {...{
      "id": "create-an-item-using-link"
    }}>{`Create an Item using Link`}</h4>
    <Callout mdxType="Callout">
      <p>{`These instructions cover Link for web applications. For instructions on using Link in mobile apps, see the `}<a parentName="p" {...{
          "href": "/docs/link"
        }}>{`Link documentation`}</a>{`.`}</p>
    </Callout>
    <p>{`Plaid Link is the client-side component that your users interact with to securely connect their bank accounts to your app. An `}<a parentName="p" {...{
        "href": "/docs/quickstart/glossary/#item"
      }}>{`Item`}</a>{` is created when a user successfully logs into their financial institution using Link. An Item represents a single login at a financial institution. Items do not represent individual bank accounts, although all bank accounts are associated with an Item. For example, if a user has a single login at a bank that allows them to access both a checking account and a savings account, only a single Item would be associated with both of the accounts.`}</p>
    <p>{`When using Auth, you will typically only need access to the specific bank account that will be used to transfer funds, rather than all of the accounts a user may have at an institution. Because of this, it is recommended that you configure Link with `}<a parentName="p" {...{
        "href": "/docs/link/customization/#account-select"
      }}>{`Account Select`}</a>{` when using Auth. Configuring Link with Account Select will limit unnecessary access to user accounts. You can `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link/account-select"
      }}>{`enable Account Select from the Dashboard`}</a>{`.`}</p>
    <p>{`To create an Item, you'll first need to create a `}<a parentName="p" {...{
        "href": "/docs/quickstart/glossary/#link-token"
      }}>{`Link token`}</a>{` on your application server. You can create a Link token by calling the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` endpoint. Then, on the client-side of your application, you'll initialize Link using the Link token you created.`}</p>
    <p>{`The code samples below demonstrate how to create a Link token and how to initialize Link using the token.`}</p>
    <h5 {...{
      "id": "create-a-link-token"
    }}>{`Create a Link token`}</h5>
    <SampleCode.CreateLinkToken product='auth' />
    <h5 {...{
      "id": "initialize-link-with-the-link-token"
    }}>{`Initialize Link with the Link token`}</h5>
    <Callout mdxType="Callout">
      <p>{`If your use case involves a pay-by-bank payments flow where the end user can choose between paying via a credit card and paying via a bank account, it is highly recommended to use the `}<a parentName="p" {...{
          "href": "/docs/link/embedded-institution-search/"
        }}>{`Embedded experience`}</a>{` for Link to increase uptake of pay-by-bank. If your use case is an account opening or funding flow that requires the customer to use a bank account, or has a surcharge for credit card use, use the standard Link experience.`}</p>
    </Callout>
    <ContentSwitcher.Container id='auth_link' names={['Account funding (standard Link)', 'Pay by Bank (Embedded Link)']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false #app.js",
            "switcher": "false",
            "#app.js": true
          }}>{`const handler = Plaid.create({
  token: (await $.post('/create_link_token')).link_token,
  onSuccess: (public_token, metadata) => {
    // Upon successful linking of a bank account,
    // Link will return a public token.
    // Exchange the public token for an access token
    // to make calls to the Plaid API.
    $.post('/exchange_public_token', {
      public_token: public_token,
    });
  },
  onLoad: () => {},
  onExit: (err, metadata) => {
    // Optionally capture when your user exited the Link flow.
    // Storing this information can be helpful for support.
  },
  onEvent: (eventName, metadata) => {
    // Optionally capture Link flow events, streamed through
    // this callback as your users connect an Item to Plaid.
  },
});

handler.open();
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <p>{`The sample code below is for JavaScript. For more examples for other platforms, see `}<a parentName="p" {...{
            "href": "/docs/link/embedded-institution-search/#integration-steps"
          }}>{`Embedded Link`}</a>{`.`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-html",
            "metastring": "switcher=false #Embedded Institution search - web (HTML)",
            "switcher": "false",
            "#Embedded": true,
            "Institution": true,
            "search": true,
            "-": true,
            "web": true,
            "(HTML)": true
          }}>{`<div id="plaid-embedded-link-container"></div>
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false #Embedded Institution Search - web (JavaScript)",
            "switcher": "false",
            "#Embedded": true,
            "Institution": true,
            "Search": true,
            "-": true,
            "web": true,
            "(JavaScript)": true
          }}>{`// The container at \`#plaid-embedded-link-container\` will need to be sized in order to
// control the size of the embedded Plaid module
const embeddedLinkOpenTarget = document.querySelector('#plaid-embedded-link-container');

Plaid.createEmbedded(
    {
      token: 'GENERATED_LINK_TOKEN',
      onSuccess: (public_token, metadata) => {},
      onLoad: () => {},
      onExit: (err, metadata) => {},
      onEvent: (eventName, metadata) => {},
    },
    embeddedLinkOpenTarget,
);
`}</code></pre>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h4 {...{
      "id": "obtain-an-access-token"
    }}>{`Obtain an access token`}</h4>
    <p>{`When a user successfully links an Item via Link, the `}<a parentName="p" {...{
        "href": "/docs/link/web/#onsuccess"
      }}><inlineCode parentName="a">{`onSuccess`}</inlineCode></a>{` callback will be called. The `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback returns a `}<a parentName="p" {...{
        "href": "/docs/quickstart/glossary/#public-token"
      }}>{`public token`}</a>{`. On your application server, exchange the public token for an `}<a parentName="p" {...{
        "href": "/docs/quickstart/glossary/#access-token"
      }}>{`access token`}</a>{` and an `}<a parentName="p" {...{
        "href": "/docs/quickstart/glossary/#item-id"
      }}>{`Item ID`}</a>{` by calling `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{`. The access token will allow you to make authenticated calls to the Plaid API.`}</p>
    <p>{`Store the access token and Item ID in a secure datastore, as they're used to access Item data and identify webhooks, respectively. The access token will remain valid unless you actively expire it via rotation or remove it by calling `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{` on the corresponding Item. For security purposes, never store the access token in client-side code. The public token is a one-time use token with a lifetime of 30 minutes, so there is no need to store it.`}</p>
    <h5 {...{
      "id": "exchange-public-token-for-an-access-token"
    }}>{`Exchange public token for an access token`}</h5>
    <SampleCode.ExchangePublicToken />
    <h4 {...{
      "id": "getting-auth-data"
    }}>{`Getting Auth data`}</h4>
    <p>{`Now that you have an access token, you can begin making authenticated calls to the Plaid API. If you are processing payments yourself, call the `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` endpoint to retrieve the bank account and bank identification numbers (such as routing numbers, for US accounts) associated with an Item's accounts. You can then supply these to your payment system. If you are using a Plaid partner to move funds, you will not need to call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`. Instead, see `}<a parentName="p" {...{
        "href": "#moving-funds-with-a-payment-partner"
      }}>{`Moving funds with a payment partner`}</a>{`.`}</p>
    <MDXCodeBlock type='server' title='/auth/get' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-node",
          "metastring": "#server.js",
          "#server.js": true
        }}>{`const { AuthGetRequest } = require('plaid');

// Call /auth/get and retrieve account numbers for an Item
const request = {
  access_token: access_token,
};
try {
  const response = await plaidClient.authGet(request);
  const accountData = response.data.accounts;
  if (response.data.numbers.ach.length > 0) {
    // Handle ACH numbers (US accounts)
    const achNumbers = response.data.numbers.ach;
  }
  if (response.data.numbers.eft.length > 0) {
    // Handle EFT numbers (Canadian accounts)
    const eftNumbers = response.data.numbers.eft;
  }
  if (response.data.numbers.international.length > 0) {
    // Handle International numbers
    const internationalNumbers = response.data.numbers.international;
  }
  if (response.data.numbers.bacs.length > 0) {
    // Handle Bacs numbers (British accounts)
    const bacsNumbers = response.data.numbers.bacs;
  }
} catch (error) {
  //handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/auth/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "access_token": String
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby",
          "metastring": "#server.rb",
          "#server.rb": true
        }}>{`require 'plaid'

# Call /auth/get and retrieve account numbers for an Item
request = Plaid::AuthGetRequest.new({ access_token: access_token })
response = client.auth_get(request)
numbers = response.numbers
if numbers.ach.length > 0
  # Handle ACH numbers (US accounts)
  achNumbers = numbers.ach
end
if numbers.eft.length > 0
  # Handle EFT numbers (Canadian accounts)
  eftNumbers = numbers.eft
end
if numbers.international.length > 0
  # Handle International numbers (Standard International accounts)
  internationalNumbers = numbers.international
end
if numbers.bacs.length > 0
  # Handle Bacs numbers (British accounts)
  bacsNumbers = numbers.bacs
end
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java",
          "metastring": "#Server.java",
          "#Server.java": true
        }}>{`import com.plaid.client.model.AuthGetRequest;
import com.plaid.client.model.AuthGetResponse;

// Call /auth/get and retrieve account numbers for an Item
AuthGetRequest request = new AuthGetRequest()
  .accessToken(accessToken);

Response<AuthGetResponse> response = client()
  .authGet(request)
  .execute();
if (response.body().getNumbers().getAch().size() > 0) {
  // Handle ACH numbers (US accounts)
  for (NumbersACH numberACH :
response.body().getNumbers().getAch()) { ... }
}
if (response.body().getNumbers().getEft().size() > 0) {
  // Handle EFT numbers (Canadian accounts)
  for (NumbersEFT numberEFT :
response.body().getNumbers().getEft()) { ... }
}
if (response.body().getNumbers().getInternational().size() > 0) {
  // Handle International numbers (Standard International accounts)
  for (NumbersInternational numberInternational :
response.body().getNumbers().getInternational()) { ... }
}
if (response.body().getNumbers().getBacs().size() > 0) {
  // Handle Bacs numbers (British accounts)
  for (NumbersBACS numberBACS :
response.body().getNumbers().getBacs()) { ... }
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python",
          "metastring": "#server.py",
          "#server.py": true
        }}>{`import plaid
from plaid.model.auth_get_request import AuthGetRequest

# Call /auth/get and retrieve account numbers for an Item
request = AuthGetRequest(access_token=access_token)
response = client.auth_get(request)
numbers = response['numbers']
if len(numbers['ach']) > 0:
    # Handle ACH numbers (US accounts)
    achNumbers = numbers['ach']
if len(numbers['eft']) > 0:
    # Handle EFT numbers (Canadian accounts)
    eftNumbers = numbers['eft']
if len(numbers['international']) > 0:
    # Handle International numbers (Standard International accounts)
    internationalNumbers = numbers['international']
if len(numbers['bacs']) > 0:
    # Handle Bacs numbers (British accounts)
    bacsNumbers = numbers['bacs']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "#server.go",
          "#server.go": true
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v48/plaid"
)

// Call /auth/get and retrieve account numbers for an Item
authGetResp, _, err := client.PlaidApi.AuthGet(ctx).AuthGetRequest(
  *plaid.NewAuthGetRequest(accessToken),
).Execute()

numbers := authGetResp.GetNumbers()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern #server.go",
          "sdk": "fern",
          "#server.go": true
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v49"
)

// Call /auth/get and retrieve account numbers for an Item
authGetResp, err := client.Auth.Get(ctx, &plaid.AuthGetRequest{AccessToken: accessToken})

numbers := authGetResp.GetNumbers()
`}</code></pre>
    </MDXCodeBlock>
    <p>{`Example response data is below. Note that this is test account data; real accounts would not include all four sets of numbers.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#/auth/get sample response",
        "#/auth/get": true,
        "sample": true,
        "response": true
      }}>{`{
  "accounts": [
    {
      "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
      "balances": {
        "available": 100,
        "current": 110,
        "limit": null,
        "iso_currency_code": "USD",
        "unofficial_currency_code": null
      },
      "mask": "9606",
      "name": "Plaid Checking",
      "official_name": "Plaid Gold Checking",
      "subtype": "checking",
      "type": "depository"
    }
  ],
  "numbers": {
    "ach": [
      {
        "account": "9900009606",
        "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
        "routing": "011401533",
        "wire_routing": "021000021"
      }
    ],
    "eft": [
      {
        "account": "111122223333",
        "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
        "institution": "021",
        "branch": "01140"
      }
    ],
    "international": [
      {
        "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
        "bic": "NWBKGB21",
        "iban": "GB29NWBK60161331926819"
      }
    ],
    "bacs": [
      {
        "account": "31926819",
        "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
        "sort_code": "601613"
      }
    ]
  },
  "item": {
    "available_products": [
      "balance",
      "identity",
      "payment_initiation",
      "transactions"
    ],
    "billed_products": ["assets", "auth"],
    "consent_expiration_time": null,
    "error": null,
    "institution_id": "ins_117650",
    "item_id": "DWVAAPWq4RHGlEaNyGKRTAnPLaEmo8Cvq7na6",
    "webhook": "https://www.genericwebhookurl.com/webhook"
  },
  "request_id": "m8MDnv9okwxFNBV"
}
`}</code></pre>
    <h4 {...{
      "id": "moving-funds-with-a-payment-partner"
    }}>{`Moving funds with a payment partner`}</h4>
    <p>{`You can move money via ACH transfer by pairing Auth with one of Plaid's payment partners. When using a partner to move money, the partner's payments service will initiate the transfer; Plaid does not function as the payment processor. For the full list of payments platforms who have partnered with Plaid to provide ACH money movement, see `}<a parentName="p" {...{
        "href": "/docs/auth/partnerships"
      }}>{`Auth Partnerships`}</a>{`.`}</p>
    <p>{`To move money using a Plaid partner, first create an Item using Link and obtain an access token as described above. Then, instead of calling Plaid's Auth endpoints, call one of Plaid's `}<a parentName="p" {...{
        "href": "/docs/api/processors"
      }}>{`processor token endpoints`}</a>{` to create a processor token. You can then send this processor token to one of Plaid's partners by using endpoints that are specific to the payment platform. Refer to the partner's technical documentation for more information. Using a partner to transfer funds gives you access to payment functionality while freeing you from having to securely store sensitive bank account information.`}</p>
    <p>{`The sample code below demonstrates a call to `}<inlineCode parentName="p">{`/processor/token/create`}</inlineCode>{` using Dwolla as the payment processor.`}</p>
    <MDXCodeBlock type='server' title='Sample /processor/token/create call' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`# Create a processor token for a specific account id.
curl \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "access_token": "\${ACCESS_TOKEN}",
    "account_id": "\${ACCOUNT_ID}",
    "processor": "dwolla"
  }' \\
  -X POST \\
  https://sandbox.plaid.com/processor/token/create
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import com.plaid.client.model.ProcessorTokenCreateRequest;
import com.plaid.client.model.ProcessorTokenCreateResponse;

// Create a processor token for a specific account id
ProcessorTokenCreateRequest request = new ProcessorTokenCreateRequest()
  .accessToken(accessToken)
  .processor(ProcessorTokenCreateRequest.ProcessorEnum.DWOLLA)
  .accountId("ACCOUNT_ID");

Response<ProcessorTokenCreateResponse> dwollaResponse = client()
  .processorTokenCreate(request)
  .execute();

String dwollaProcessorToken = dwollaResponse.body().getProcessorToken();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`require 'plaid'
# Create a processor token for a specific account id
processor_token_create_request = Plaid::ProcessorTokenCreateRequest.new({
    access_token: access_token,
    account_id: account_id,
    processor: "dwolla"
  }
)

create_response = client.processor_token_create(processor_token_create_request)
processor_token = create_response.processor_token
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`from plaid.model.processor_token_create_request import ProcessorTokenCreateRequest

# Create a processor token for a specific account id
create_request = ProcessorTokenCreateRequest(
    access_token=access_token,
    account_id=account_id,
    processor='dwolla'
)
create_response = client.processor_token_create(create_request)
processor_token = create_response['processor_token']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const { ProcessorTokenCreateRequest } = require('plaid');

try {
  // Create a processor token for a specific account id
  const request: ProcessorTokenCreateRequest = {
    access_token: accessToken,
    account_id: accountID,
    processor: 'dwolla',
  };
  const processorTokenResponse = await plaidClient.processorTokenCreate(
    request,
  );
  const processorToken = processorTokenResponse.data.processor_token;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`import (
  "context"
  "os"

  plaid "github.com/plaid/plaid-go/v48/plaid"
)


// Get the account id
accountsResp, _, err := client.PlaidApi.AccountsGet(ctx).AccountsGetRequest(plaid.AccountsGetRequest{
  AccessToken: accessToken,
}).Execute()
accountID := accountsResp.GetAccounts()[0].GetAccountId()

// Create a dwolla token for the specific account id
request := plaid.NewProcessorTokenCreateRequest(accessToken, accountID, "dwolla")
processorTokenResp, _, err := client.PlaidApi.ProcessorTokenCreate(ctx).ProcessorTokenCreateRequest(
  *request,
).Execute()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`import (
    "context"
    "os"

    plaid "github.com/plaid/plaid-go/v49"
)

// Get the account id
accountsResp, err := client.Accounts.Get(ctx, &plaid.AccountsGetRequest{AccessToken: accessToken})
accountID := accountsResp.GetAccounts()[0].GetAccountID()

// Create a dwolla token for the specific account id
request := &plaid.ProcessorTokenCreateRequest{
    AccessToken: accessToken,
    AccountID:   accountID,
    Processor:   "dwolla",
}
processorTokenResp, err := client.Processor.CreateToken(ctx, request)
`}</code></pre>
    </MDXCodeBlock>
    <h4 {...{
      "id": "example-code-in-plaid-pattern"
    }}>{`Example code in Plaid Pattern`}</h4>
    <p>{`For a real-life example of an app that incorporates Auth, see the Node-based `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern-account-funding"
      }}>{`Plaid Pattern Account Funding`}</a>{` sample app. Pattern Account Funding is a sample account funding app that fetches Auth data in order to set up funds transfers. The Auth code can be found in `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern-account-funding/blob/master/server/routes/items.js#L133-L195"
      }}>{`items.js`}</a>{`.`}</p>
    <h4 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h4>
    <p>{`Once Auth is implemented in your app, see `}<a parentName="p" {...{
        "href": "/docs/auth/coverage"
      }}>{`Additional verification methods`}</a>{` to make sure your app is supporting the maximum number of institutions and verification methods (US and CA only).`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "get-plaid-api-keys-and-complete-application-and-company-profile",
        level: 4,
        title: "Get Plaid API keys and complete application and company profile",
      },
{
        id: "install-and-initialize-plaid-libraries",
        level: 4,
        title: "Install and initialize Plaid libraries",
      },
{
        id: "create-an-item-using-link",
        level: 4,
        title: "Create an Item using Link",
      },
{
        id: "obtain-an-access-token",
        level: 4,
        title: "Obtain an access token",
      },
{
        id: "getting-auth-data",
        level: 4,
        title: "Getting Auth data",
      },
{
        id: "moving-funds-with-a-payment-partner",
        level: 4,
        title: "Moving funds with a payment partner",
      },
{
        id: "example-code-in-plaid-pattern",
        level: 4,
        title: "Example code in Plaid Pattern",
      },
{
        id: "next-steps",
        level: 4,
        title: "Next steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
